"use client";
import { useRouter } from "next/navigation";
import swal from "sweetalert";
import { errorHandler } from "@/config/errorReporting";
import {
	ERROR_CUENTA_DE_MEDICO,
	ERROR_SIN_PERMISOS,
} from "@/modules/user/infraestructure/services/auth";
import { useSignIn } from "@/modules/user/infraestructure/services/auth";
import Image from "next/image";
import { CardContainer } from "src/storybook/components/cards/Utils/Card";
import { Button, Loader, Title } from "@umahealth/occipital";
import { Input } from "@umahealth/occipital/client";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useEffect } from "react";
import { auth } from "@/config/firebaseApp";
import { removeAuthCookies } from "@/modules/users/service/auth";
import { useUpdateTokens } from "../services/useUpdateTokens";

/**
 * A dónde mandar a un médico que entró al portal equivocado.
 *
 * La variable ya existía en los `.env` de los tres ambientes y no la leía
 * nadie. Si faltara, el aviso se muestra igual pero sin botón: es preferible a
 * ofrecer un link roto.
 */
const urlDelConsultorio = process.env.NEXT_PUBLIC_ONLINEDOCTOR_URL ?? "";

export default function LoginComponent({
	logoSrc,
	searchParams,
}: {
	logoSrc: string;
	searchParams?: {
		customToken?: string;
		redirect?: string;
		verifyCache?: "true";
	};
}): JSX.Element {
	const router = useRouter();
	const signIn = useSignIn();

	const updateTokens = useUpdateTokens({
		options: {
			onSuccess: () => {
				router.push(searchParams?.redirect ?? "/");
			},
			onError: async () => {
				localStorage.clear();
				removeAuthCookies();
				signOut(auth);
				window.location.replace("/usuarios/acceso");
			},
		},
	});

	useEffect(() => {
		if (searchParams?.verifyCache === "true") {
			const authStateChange = onAuthStateChanged(auth, (user) => {
				if (user) {
					updateTokens.mutate(user);
				} else {
					router.replace("/usuarios/acceso");
				}
			});
			return () => authStateChange();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth, searchParams?.verifyCache]);

	async function submitSignIn(e: React.SyntheticEvent) {
		e.preventDefault();
		const target = e.target as typeof e.target & {
			email: { value: string };
			password: { value: string };
		};
		const email = target.email.value;
		const password = target.password.value;

		if (email.length < 5 || password.length < 5) {
			return;
		}

		signIn.mutate(
			{
				email: email,
				password: password,
			},
			{
				onSuccess: (user) => {
					if (user?.uid) {
						router.push("/operaciones");
					}
				},
				onError: (error) => {
					let popTitle = "Ocurrió un problema";
					let popMessage;
					let esCuentaDeMedico = false;

					/**
					 * Firebase unificó los códigos de credenciales.
					 *
					 * Antes devolvía `auth/user-not-found` y `auth/wrong-password`
					 * por separado; con la protección contra enumeración de
					 * usuarios —que viene activada— devuelve un solo
					 * `auth/invalid-login-credentials`, y en versiones más nuevas
					 * `auth/invalid-credential`. Ninguno de los dos entraba acá, así
					 * que caían en el `else` y la pantalla mostraba el texto crudo:
					 * «Firebase: Error (auth/invalid-login-credentials)».
					 *
					 * Eso no le dice nada a nadie. El 7 de septiembre tres personas
					 * lo vieron a la vez y la conclusión fue «se cayó UMA», cuando
					 * el resto estaba entrando sin problemas.
					 *
					 * El mensaje no distingue si falló el email o la contraseña, a
					 * propósito: es lo mismo que hace Firebase para no revelar qué
					 * cuentas existen.
					 */
					if (
						error.message.includes("auth/invalid-login-credentials") ||
						error.message.includes("auth/invalid-credential") ||
						error.message.includes("auth/user-not-found") ||
						error.message.includes("auth/wrong-password")
					) {
						popTitle = "Email o contraseña incorrectos";
						popMessage =
							"Revisá los datos e intentá de nuevo. Si no los recordás, pedile a un supervisor que te los restablezca.";
					} else if (error.message.includes("auth/too-many-requests")) {
						/**
						 * Firebase bloquea la cuenta un rato después de varios
						 * intentos fallidos, y se destraba solo. Sin decirlo, el
						 * síntoma es "no puedo entrar" durante minutos y después
						 * "ya anda", que es indistinguible de una caída.
						 */
						popTitle = "Demasiados intentos";
						popMessage =
							"Por seguridad, el acceso quedó bloqueado unos minutos después de varios intentos fallidos. Esperá un momento y volvé a probar.";
					} else if (error.message.includes("auth/user-disabled")) {
						popTitle = "Cuenta deshabilitada";
						popMessage =
							"Esta cuenta está deshabilitada. Comunicate con un supervisor.";
					} else if (
						error.message.includes("auth/network-request-failed")
					) {
						popTitle = "Sin conexión";
						popMessage =
							"No pudimos conectarnos. Revisá la conexión e intentá de nuevo.";
					} else if (error.message === ERROR_CUENTA_DE_MEDICO) {
						/*
						 * Una cuenta que es sólo de médico no trabaja en el portal
						 * de gestión. En vez de dejarla con un "no pudimos iniciar
						 * sesión", se le ofrece el link del consultorio, que es su
						 * lugar.
						 */
						esCuentaDeMedico = true;
						popTitle = "Esta cuenta es de un médico";
						popMessage =
							"El portal de gestión es para supervisión y enfermería. Para atender y firmar evaluaciones, entrá a la app del consultorio.";
					} else if (error.message === ERROR_SIN_PERMISOS) {
						popTitle = "Permisos insuficientes";
						popMessage =
							"Esta cuenta no tiene permisos para entrar al portal. Pedile a un supervisor que te los asigne.";
					} else {
						// Nunca el texto crudo de Firebase en pantalla: no es
						// accionable y se lee como una falla del sistema. El detalle
						// va al reporte de errores, que es donde sirve.
						popMessage =
							"No pudimos iniciar sesión. Intentá de nuevo en un momento.";
						errorHandler?.report(
							`Error iniciando sesión: ${error}`
						);
					}

					if (esCuentaDeMedico && urlDelConsultorio) {
						/*
						 * Este caso tiene a dónde ir, así que lleva botón y no
						 * temporizador: cerrarse solo en 4 segundos le sacaría de
						 * encima justo la única acción útil.
						 */
						swal({
							title: popTitle,
							text: popMessage,
							icon: "info",
							buttons: ["Cerrar", "Ir al consultorio"],
						}).then((ir) => {
							if (ir) window.location.href = urlDelConsultorio;
						});
					} else {
						swal({
							title: popTitle,
							text: popMessage,
							timer: 4000,
							buttons: {
								confirm: false,
							},
							icon: "warning",
						});
					}
				},
			}
		);
	}

	if (
		searchParams?.customToken ||
		searchParams?.verifyCache ||
		updateTokens.isSuccess ||
		updateTokens.isPending
	) {
		return (
			<div className="h-full grid place-content-center justify-center h gap-4">
				<Loader
					className="m-auto"
					size="size-7"
					color="stroke-primary"
				/>
				<p className="text-center text-primary font-semibold">Verificando sesión...</p>
			</div>
		);
	}


	return (
		<CardContainer className="border-none shadow-shortDefault max-w-[592px] p-8 h-3/4">
			<div className="w-full flex flex-col justify-center items-center">
				<div className="w-full flex flex-col justify-center items-center mb-16 pt-24">
					<Image src={logoSrc} alt="logo trenes argentinos operaciones" height={200} width={200} />
				</div>
				<Title
					color="text-primary-800"
					hierarchy="h1"
					weight="font-bold"
					size="text-l"
					className="w-full mb-10"
				>
					Inicio de sesión
				</Title>
				<form
					className="flex flex-col w-full"
					onSubmit={(e) => submitSignIn(e)}
				>
					<Input
						name="email"
						type="email"
						placeholder="test@gmail.com"
						label="Ingresá tu correo electrónico"
						classNameContainer="mt-0"
						autoComplete="email"
						inputMode='email'
						autoFocus
						required
					/>
					<Input
						name="password"
						type="password"
						autoComplete="current-password"
						label="Ingresá tu contraseña"
						required
					/>
					<Button
						type="submit"
						className="button mt-6 mb-24 w-full max-w-72 self-center bg-primary-700 hover:bg-primary-800"
						loading={signIn.isPending || signIn.isSuccess}
					>
						Iniciar sesión
					</Button>
				</form>
			</div>
		</CardContainer>
	);
}
